import {
  useGetOrganisationQuery,
  useGetOrganisationsQuery,
} from '@/app/redux/endpoints/organisation';
import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useUser } from '@/app/redux/hooks/user';
import { selectClaims, setClaims } from '@/app/redux/slices/sessionSlice';
import { FeatureEntitlements } from '@/common/types/organisation';
import { refreshUserClaims } from '@/common/utils/auth';
import { TRIAL_ENTITLEMENTS } from '@/common/utils/entitlements';
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  saveToLocalStorage,
} from '@/common/utils/localStorage';
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'currentOrganisationId';

let sharedOrgId: string | null = getFromLocalStorage(STORAGE_KEY);
const listeners = new Set<(orgId: string | null) => void>();

function setCurrentOrganisationId(orgId: string | null) {
  sharedOrgId = orgId;

  if (orgId) saveToLocalStorage(STORAGE_KEY, orgId);
  else removeFromLocalStorage(STORAGE_KEY);

  listeners.forEach((fn) => fn(orgId));
}

export function useCurrentOrganisationId() {
  const [currentOrganisationId, setLocal] = useState<string | null>(
    sharedOrgId,
  );

  useEffect(() => {
    const fn = (orgId: string | null) => setLocal(orgId);
    listeners.add(fn);
    // Sync in case sharedOrgId changed between useState init and listener registration
    setLocal(sharedOrgId);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  const switchOrganisation = useCallback((orgId: string | null) => {
    setCurrentOrganisationId(orgId);
  }, []);

  return { currentOrganisationId, switchOrganisation };
}

export function useInitializeOrganisation() {
  const dispatch = useAppDispatch();
  const { user } = useUser();
  const claims = useAppSelector(selectClaims);

  const { currentOrganisationId, switchOrganisation } =
    useCurrentOrganisationId();

  // True until we've resolved which org to use (or determined there are none)
  const isInitializing =
    !!user && (user.organisationIds ?? []).length > 0 && !currentOrganisationId;

  useEffect(() => {
    if (!user) return;

    const userOrgIds = user.organisationIds ?? [];
    if (!userOrgIds.length) {
      if (currentOrganisationId !== null) switchOrganisation(null);
      return;
    }

    // Only switch to orgs present in auth token claims. The Firestore user
    // subscription can push an updated organisationIds before the token has
    // been refreshed (refreshUserClaims), which would cause org-scoped queries
    // to fire with stale claims and get rejected by security rules.
    const claimOrgIds = (claims?.organisationIds as string[] | undefined) ?? [];
    const validOrgIds = userOrgIds.filter((id) => claimOrgIds.includes(id));
    if (!validOrgIds.length) {
      // Claims out of sync — user has orgs in Firestore but none are in token claims.
      // Trigger a refresh so we don't get stuck.
      refreshUserClaims((c) =>
        userOrgIds.some(
          (id) =>
            (c?.organisationIds as string[] | undefined)?.includes(id) ?? false,
        ),
      ).then((claims) => {
        if (claims) dispatch(setClaims(claims));
      });
      return;
    }

    const targetOrgId =
      currentOrganisationId && validOrgIds.includes(currentOrganisationId)
        ? currentOrganisationId
        : validOrgIds[0];

    if (currentOrganisationId !== targetOrgId) {
      switchOrganisation(targetOrgId);
    }
  }, [user, claims, currentOrganisationId, switchOrganisation, dispatch]);

  return { isInitializing };
}

export function useOrganisation() {
  const { user } = useUser();
  const userOrgIds = user?.organisationIds ?? [];

  const { currentOrganisationId, switchOrganisation: _switchOrganisation } =
    useCurrentOrganisationId();

  const {
    data: organisation = null,
    isLoading,
    isFetching,
    error,
  } = useGetOrganisationQuery(currentOrganisationId ?? '', {
    skip: !currentOrganisationId,
  });

  const {
    data: allOrganisations = [],
    isLoading: isLoadingAllOrgs,
    error: allOrgsError,
  } = useGetOrganisationsQuery(userOrgIds, { skip: !userOrgIds.length });

  const entitlements: FeatureEntitlements =
    organisation?.license?.entitlements ?? TRIAL_ENTITLEMENTS;

  const switchOrganisation = useCallback(
    (orgId: string) => _switchOrganisation(orgId),
    [_switchOrganisation],
  );

  return {
    organisation,
    entitlements,
    isLoading,
    isFetching,
    error,

    allOrganisations,
    isLoadingAllOrgs,
    allOrgsError,

    currentOrganisationId,
    switchOrganisation,
  };
}
